import Layout from '@app-studio-web/ui/components/Layout'
import * as Sentry from '@sentry/react'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Form, useSubmit } from 'react-router-dom'

type ButtonOption = {
  text: React.ReactNode | string
  subtext?: string
  value: string
}

export default function QuestionEmotionalAwarenessPage() {
  const { t } = useTranslation()
  const submit = useSubmit()
  const [clickedValue, setClickedValue] = useState<string | null>(null)

  const heading = t('pages.onboarding.emotionalAwareness.heading')
  const subheading = t('pages.onboarding.emotionalAwareness.subheading')
  const buttons: ButtonOption[] = useMemo(
    () => [
      {
        text: t('pages.onboarding.emotionalAwareness.option1'),
        subtext: t('pages.onboarding.emotionalAwareness.option1Sub'),
        value: 'very-aware',
      },
      {
        text: t('pages.onboarding.emotionalAwareness.option2'),
        subtext: t('pages.onboarding.emotionalAwareness.option2Sub'),
        value: 'somewhat-aware',
      },
      {
        text: t('pages.onboarding.emotionalAwareness.option3'),
        subtext: t('pages.onboarding.emotionalAwareness.option3Sub'),
        value: 'not-very-aware',
      },
      {
        text: t('pages.onboarding.emotionalAwareness.option4'),
        subtext: t('pages.onboarding.emotionalAwareness.option4Sub'),
        value: 'not-aware',
      },
    ],
    [t],
  )

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const hiddenInput = document.createElement('input')
    hiddenInput.type = 'hidden'
    hiddenInput.name = 'answer'
    hiddenInput.value = clickedValue || ''
    event.currentTarget.appendChild(hiddenInput)

    if (clickedValue) {
      try {
        sessionStorage.setItem('onboarding_emotional_awareness', clickedValue)
      } catch (error) {
        Sentry.captureException(error)
      }
    }

    submit(event.currentTarget)
    event.currentTarget.removeChild(hiddenInput)
  }

  return (
    <Layout>
      <div className="fixed inset-0 -z-10 bg-gradient-to-b from-[#FFFFFF] to-[#EFFBFF]" />
      <div className="flex h-full flex-col">
        <div className="mt-11 shrink-0 text-center">
          <h1 className="custom-heading mb-2" data-testid="label-header">
            {heading}
          </h1>
          <p className="text-gray-600">{subheading}</p>
        </div>

        <div className="flex grow items-end justify-center pb-10">
          <Form
            method="post"
            className="w-full max-w-md"
            onSubmit={handleSubmit}
          >
            <div className="flex flex-col space-y-3">
              {buttons.map((button, index) => (
                <button
                  key={`button-${index}`}
                  type="submit"
                  className={`w-full cursor-pointer rounded-[20px] border bg-white px-6 py-4 text-left text-base transition-all hover:shadow-md ${
                    clickedValue === button.value
                      ? 'border-2 border-blue-400 bg-blue-50'
                      : 'border-gray-200'
                  }`}
                  data-testid={`button-option-${index + 1}`}
                  onClick={() => setClickedValue(button.value)}
                >
                  <p className="font-semibold">{button.text}</p>
                  {button.subtext && (
                    <p className="mt-1 text-sm text-gray-500">
                      {button.subtext}
                    </p>
                  )}
                </button>
              ))}
            </div>
          </Form>
        </div>
      </div>
    </Layout>
  )
}
